import type { HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';
import { PromotionComponentLayout1Messages } from './messages';
import { Content } from '../Content';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking';
import { BadgeDV } from '../../../BadgeDV';
import { OutlinedCard } from '../../../OutlinedCard';
import {
  ContentWrapper,
  ContentWrapperCallToAction,
  ContentWrapperContent,
} from '../ContentWrapper';

export type Layout1Props = HTMLAttributes<HTMLDivElement> & {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component Layout 1
 *
 * component to promote the installment calculator in layout version 1
 *
 * */
export const Layout1 = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  openInstallmentCalulatorHandler,
  ...props
}: Layout1Props) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  const handleOutlineCardAction = () => {
    openInstallmentCalulatorHandler();
    trackGlycerinLinkClick();
  };

  return (
    <OutlinedCard
      {...props}
      className="relative mt-[8px] cursor-default p-0"
      onClick={handleOutlineCardAction}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleOutlineCardAction();
        }
      }}
      aria-haspopup="dialog"
      aria-label={formatMessage(PromotionComponentLayout1Messages.CtA)}
      role="button"
      tabIndex={0}
    >
      <div
        className="bg-color-background-base absolute left-1/2 top-0 px-1"
        style={{
          transform: 'translate(-50%, -50%)',
        }}
      >
        <BadgeDV text={formatMessage(PromotionComponentLayout1Messages.ChipLabel)} />
      </div>

      <ContentWrapper>
        <ContentWrapperContent>
          <Content
            smallText={formatMessage(PromotionComponentLayout1Messages.From)}
            bigText={formatPrice(singleInstallment, currency)}
          />
          <Content
            smallText={formatMessage(PromotionComponentLayout1Messages.InstallmentsPrefix)}
            bigText={formatMessage(PromotionComponentLayout1Messages.Installments, {
              installments: numberOfInstallments,
            })}
          />
        </ContentWrapperContent>
        <ContentWrapperCallToAction>
          {formatMessage(PromotionComponentLayout1Messages.CtA)}
        </ContentWrapperCallToAction>
      </ContentWrapper>
    </OutlinedCard>
  );
};
